/**
 * 形象页（/me/look）上「TA 的身体特点」那张卡和它打开的底部面板（WIRE-BODY-01，总账 N-05 的 A1；I 10-09 照 6c2b 推荐定六条，第 4 条定甲）。
 * 后端早有身体档案的读口和五个写命令（BODY-0 §11），页面一直没接：形象卡在 body_info_needed 时只说「身体档案还缺一些信息」，主人无处可补。
 * - 入口（I 定第 1 条）：形象页在形象卡在 body_info_needed 时出这张卡，按 reason_details.group 写「补一下 TA 的腿的情况，就能画全身。」，
 *   点开是底部面板（共享 Sheet，和这一页「查看内容使用说明」同一个）。到家等待页只写一句话加一个来这一页的链接（ArrivingPage）。
 *   首期不在设置里加常驻入口。只给 can_edit 的人看入口（原创居民、没有照顾权限的成员都是 false，后端算好）。
 * - 常驻入口（I 10-15，A-BODYINFO-ENTRY-116）：形象没卡着、也没有待确认和冲突时，家庭管理员（role admin 且 can_edit）在这里只看到一行
 *   「记一下 TA 的身体特点」，不是卡片、不弹、不提醒；点开同一个面板，先选「想记哪一处？」，再整组问（record 写入位，和卡着时的 submit 分开核）。
 *   照护面板要先有「已确认没有」的部位才能提「补回来」，这一行就是平时记它的地方。成员照旧只在卡着时看到入口。
 * - 只问缺的那一组（第 2 条）：先问数量，和满数不同时再逐个问部位（bodyInfoModel）。狗的大小只在面板打开时顺带问一次（第 7 条）。
 * - 部位图示（第 3 条，ASSET-UI-032）：show_body_diagram 为真、又在逐个问部位时才放；图 aria-hidden，叠在图上的 44px 点按区也 aria-hidden、
 *   不进 Tab 顺序，点一下只是把文字列表里那一处选出来、焦点落到那一行；读屏、键盘都走文字列表（「左前腿（TA 自己的左边）」）。
 * - 记下以后（第 4 条）：管理员的当场生效，成员的「已交给家里的管理员确认。」。管理员那一句分两版，按能力表 character.body_resume 切
 *   （A-BODY-RESUME-01 加的能力位，available＝补完会自动接着画）：可用才说「记下了，TA 的全身会接着画。」，否则不许诺自动。
 * - 待确认和冲突（第 5、6 条）：都在同一个面板里，只给管理员（can_confirm）。待确认逐条「确认」「先不记」；冲突首期最小版，
 *   文案照 §11.3，「按档案」「按照片改」，不说照片里看到了什么。不另开页、不推送。
 * - 写入不触发任何生成（§11.2）：这里只发身体档案的写命令，然后刷新档案和形象两条只读查询；接不接着画由后端定。
 * - 每个写命令带幂等键：同一个请求（动作＋目标＋内容）沿用同一把；有了明确回音（成功、被拒）就换新；结果不明（断网、超时、服务端出错）
 *   不换、不自动重发，主人再点是同一次（后端认得出，不会重复记）。
 * - 能力表 pets.body_profile 不是可用、服务没有这几个方法、演示模式：整张卡不出现，也不发请求。
 */
import { useEffect, useId, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { BodyDogSize, BodyPartId, BodyPlan, BodyProfileView, BodyStatementItem, BodyStatementResult, BodyStatementState, CharacterState } from "@/shared/contracts";
import { toApiError } from "@/shared/api/errors";
import { newIdempotencyKey } from "@/shared/api/idempotency";
import { env } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";
import type { ServiceMap } from "@/shared/services/types";
import { useServices } from "@/shared/services/registry";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import { Button, Sheet } from "@/shared/ui";
import { characterReasonCode } from "@/features/home/PetFigure";
import {
  BODY_CARD_TITLE, BODY_CONFIRM, BODY_CONFIRMED, BODY_CONFLICT_LINE, BODY_CORRECT, BODY_DIAGRAM_CAPTION, BODY_DIAGRAM_LEFT, BODY_DIAGRAM_RIGHT,
  BODY_DIAGRAM_SRC, BODY_DOG_QUESTION, BODY_DOG_SIZE_NAME, BODY_ERROR_CHANGED, BODY_ERROR_DONE, BODY_ERROR_FORBIDDEN, BODY_ERROR_INVALID,
  BODY_ENTRY, BODY_ERROR_UNSURE, BODY_GROUP_NAME, BODY_KEEP, BODY_KEPT, BODY_MISMATCH, BODY_OPEN_ASK, BODY_OPEN_REVIEW, BODY_PENDING_TITLE, BODY_PICK_GROUP,
  BODY_READ_FAILED, BODY_RECORD_NOTE, BODY_REJECT,
  BODY_REJECTED, BODY_RETRY, BODY_SAVED, BODY_SAVED_MORE, BODY_SAVED_RESUME, BODY_SENT_TO_ADMIN, BODY_SIDE_NOTE, BODY_STATE_NAME, BODY_SUBMIT,
  bodyAskLine, bodyAskOf, bodyAsksDogSize, bodyCorrectQuestion, bodyCountOption, bodyCountQuestion, bodyDefaultCount, bodyDetailsOf,
  bodyFormCheck, bodyItemsOf, bodyPartLabel, bodyPartsQuestion, bodyPartsShown, bodyQueryKey, bodyRecordAskOf, bodyRecordGroups, bodySpotsFor,
  bodyStatementText, bodyStillUnknown, bodyToConfirmLine, type BodyAsk, type BodyForm,
} from "./bodyInfoModel";
import "./body-info.css";

/** 形象此刻卡在 body_info_needed、细节里还有不知道的部位：要补哪一组的哪几处；其余为 null（和「调整形象」一节同一个取法 characterReasonCode） */
export function bodyNeedOf(state: CharacterState): { group: string; partIds: BodyPartId[] } | null {
  return characterReasonCode(state) === "body_info_needed" ? bodyDetailsOf(state) : null;
}

/** 管理员的一次写入生效以后说哪一句：卡着之前没要补 → 记下了；还有不知道的 → 还有一处；补全了 → 按能力表分两版 */
export function bodyAppliedLine(view: BodyProfileView, neededBefore: boolean, resumeOn: boolean): string {
  if (!neededBefore) return BODY_CONFIRMED;
  if (bodyStillUnknown(view)) return BODY_SAVED_MORE;
  return resumeOn ? BODY_SAVED_RESUME : BODY_SAVED;
}

/** 写命令出错时说的话（不露原因码）；changed＝档案在别处变了或这一条已处理，要重读一次档案 */
export function bodyErrorOf(error: unknown): { text: string; reread: boolean; sure: boolean } {
  const err = toApiError(error);
  const reason = err.details?.reason;
  const sure = err.kind === "http" && err.status !== null && err.status >= 400 && err.status < 500;
  if (reason === "body_revision_conflict" || err.code === "VERSION_CONFLICT") return { text: BODY_ERROR_CHANGED, reread: true, sure };
  if (reason === "body_statement_not_pending" || reason === "body_conflict_not_open") return { text: BODY_ERROR_DONE, reread: true, sure };
  if (reason === "body_value_invalid") return { text: BODY_ERROR_INVALID, reread: false, sure };
  if (reason === "body_statement_forbidden" || reason === "body_resident_managed") return { text: BODY_ERROR_FORBIDDEN, reread: false, sure };
  if (!sure) return { text: BODY_ERROR_UNSURE, reread: false, sure };
  return { text: err.playerMessage, reread: false, sure };
}

type Write = { owner: ServiceMap["pets"]; slot: string; run: (key: string) => Promise<BodyProfileView | BodyStatementResult>; done: (result: BodyProfileView | BodyStatementResult) => void };
type Journal = { command?: { write: Write; key: string }; busy: boolean; uncertain: boolean };
// Memory only. An unresolved command survives closing/reopening its sheet. Each confirmed
// member/household/pet has a separate journal; no cookie, storage or generated-contract change.
const journals = new Map<string, Journal>();
export function bodyWriteAllowed(view: BodyProfileView | undefined, role: string | undefined, available: boolean, slot: string): boolean {
  if (!available || !view?.can_edit) return false;
  const [action, target, rev] = slot.split(":");
  const revision = action === "submit" || action === "record" ? Number(target) : Number(rev);
  if (revision !== (view.reality_revision ?? 0)) return false;
  if (action === "submit") return true;
  if (action === "record") return role === "admin";  // 常驻入口只给管理员（I 10-15）
  if (role !== "admin" || !view.can_confirm) return false;
  if (action === "confirm" || action === "reject") return view.pending_statements.some(s => encodeURIComponent(s.statement_id) === target);
  if (action === "keep" || action === "correct") return view.conflicts.some(c => encodeURIComponent(c.conflict_id) === target);
  return false;
}
export function checkedBody(view: BodyProfileView, petId: string): BodyProfileView {
  // gen108 has NO pet_id. Request scope + abort/identity epoch binds this response.
  // An optional extra identity is checked; its absence is never presented as ID proof.
  if ("pet_id" in view && view.pet_id !== petId) throw new Error("body response identity mismatch");
  return view;
}

function useBodyWrite(petId: string, onView: (view: BodyProfileView) => void, journal: Journal, allowed: (slot: string) => boolean) {
  const queryClient = useQueryClient();
  const {pets} = useServices();
  const userId = useOptionalCurrentHousehold()?.userId ?? null;
  const [busy, setBusy] = useState(journal.busy);
  const [error, setError] = useState<string | null>(journal.uncertain ? BODY_ERROR_UNSURE : null);
  const latest = useRef({allowed, onView, userId, pets});latest.current = {allowed, onView, userId, pets};
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const send = async (write: Write, retry = false) => {
    // Synchronous guard is independent of disabled DOM controls.
    if (journal.busy || write.owner !== latest.current.pets || !latest.current.allowed(write.slot)) { setError(BODY_ERROR_FORBIDDEN); return; }
    if (journal.uncertain && !retry) { setError(BODY_ERROR_UNSURE); return; }
    const command = retry ? journal.command : {write, key: newIdempotencyKey("pet-body")};
    if (!command || command.write.owner !== latest.current.pets || !latest.current.allowed(command.write.slot)) { setError(BODY_ERROR_FORBIDDEN); return; }
    journal.command = command;journal.busy = true;setBusy(true);setError(null);
    try {
      const result = await command.write.run(command.key);
      const next = checkedBody("body" in result ? result.body : result, petId);
      journal.command = undefined;journal.uncertain = false;
      if (alive.current && command.write.owner === latest.current.pets && latest.current.allowed(command.write.slot)) {
        latest.current.onView(next);
        if (latest.current.userId) void queryClient.invalidateQueries({queryKey: queryKeys.characterFor(latest.current.userId, petId)});
        command.write.done(result);
      }
    } catch (failure) {
      const said = bodyErrorOf(failure);
      journal.uncertain = !said.sure;
      if (said.sure) journal.command = undefined;
      if (alive.current) {
        setError(said.text);
        if (said.reread) void queryClient.invalidateQueries({queryKey: bodyQueryKey(latest.current.userId, petId)});
      }
    } finally {
      journal.busy = false;if (alive.current) setBusy(false);
    }
  };
  return {send: (write: Write) => void send(write), busy, error, uncertain: journal.uncertain,
    retry: () => { if (journal.command) void send(journal.command.write, true); }};
}

export function BodyInfoCard({petId, state}: {petId: string;state: CharacterState}) {
  const ctx = useOptionalCurrentHousehold();
  const identity = JSON.stringify([ctx?.userId, ctx?.household?.household_id, petId]);
  if (!ctx?.userId || !ctx.household || ctx.pet?.pet_id !== petId || state.pet_id !== petId) return null;
  let journal = journals.get(identity);
  if (!journal) { journal = {busy: false, uncertain: false};journals.set(identity, journal); }
  return <ScopedBodyInfoCard key={identity} petId={petId} state={state} journal={journal} />;
}

function ScopedBodyInfoCard({ petId, state, journal }: { petId: string; state: CharacterState; journal: Journal }) {
  const { platform, pets } = useServices();
  const queryClient = useQueryClient();
  const userId = useOptionalCurrentHousehold()?.userId ?? null;
  const live = env.dataMode === "live";
  const meta = useQuery({ queryKey: queryKeys.meta, queryFn: () => platform.meta(), staleTime: 60_000, enabled: live });
  const on = (key: string) => meta.data?.capabilities.some((capability) => capability.key === key && capability.status === "available") === true;
  const ctx = useOptionalCurrentHousehold();
  const readable = live && Boolean(userId) && !meta.isError && on("pets.body_profile") && typeof pets.bodyProfile === "function";
  const current = useRef({ctx, pets, readable, need: bodyNeedOf(state)});current.current = {ctx, pets, readable, need: bodyNeedOf(state)};
  const allowed = (slot: string) => {
    const at = current.current;
    const currentMeta = queryClient.getQueryData<typeof meta.data>(queryKeys.meta);
    const state = queryClient.getQueryState(bodyQueryKey(userId, petId));
    const method = ({submit: "submitBody", record: "submitBody", confirm: "confirmBody", reject: "rejectBody", keep: "keepBodyConflict", correct: "correctBodyConflict"} as const)[slot.split(":")[0] as "submit"];
    return (slot.startsWith("submit:") ? at.need !== null : true) && at.pets === pets && at.ctx?.userId === userId && at.ctx?.pet?.pet_id === petId && at.readable && state?.status === "success"
      && queryClient.getQueryState(queryKeys.meta)?.status === "success" && currentMeta?.capabilities.some(c => c.key === "pets.body_profile" && c.status === "available") === true
      && typeof at.pets[method] === "function" && bodyWriteAllowed(state.data as BodyProfileView | undefined, at.ctx?.household?.role, true, slot);
  };
  const body = useQuery({
    queryKey: bodyQueryKey(userId, petId),
    queryFn: async ({ signal }) => {
      if (!current.current.readable || current.current.ctx?.userId !== userId || current.current.ctx?.pet?.pet_id !== petId) throw new Error("body read scope expired");
      const result = await pets.bodyProfile!(petId, signal);
      if (signal.aborted || current.current.ctx?.userId !== userId || current.current.ctx?.pet?.pet_id !== petId) throw new Error("body read scope expired");
      return checkedBody(result, petId);
    },
    enabled: readable,
    staleTime: 15_000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const need = bodyNeedOf(state);
  const [open, setOpen] = useState(false);
  const [recording, setRecording] = useState(false);  // 这次面板是从常驻那一行打开的
  const [said, setSaid] = useState<string | null>(null);
  const saidRef = useRef<HTMLParagraphElement>(null);
  const titleId = useId();
  useEffect(() => {
    // 面板收起、换成卡上这一句时，焦点落到这一句（原来那颗按钮可能已经不在了），读屏听得到结果
    if (said) saidRef.current?.focus();
  }, [said]);
  if (!readable) return null;
  if (body.isError) {
    // 读不到：只在形象正卡在这里时说一声、给重试；没卡着就不打扰
    if (!need) return null;
    return (
      <section className="ps-look-card ps-body-card" aria-labelledby={titleId} data-testid="body-info">
        <h3 id={titleId}>{BODY_CARD_TITLE}</h3>
        <p className="ps-body-card__line">{BODY_READ_FAILED}</p>
        <Button variant="secondary" onClick={() => { if (current.current.readable) void body.refetch(); }}>
          {BODY_RETRY}
        </Button>
      </section>
    );
  }
  const view = body.data;
  if (!view || !view.can_edit) return null;
  const resumeOn = on("character.body_resume");
  const canSubmit = typeof pets.submitBody === "function";
  const canDecide = typeof pets.confirmBody === "function" && typeof pets.rejectBody === "function";
  const canResolve = typeof pets.keepBodyConflict === "function" && typeof pets.correctBodyConflict === "function";
  const toConfirm = ctx?.household?.role === "admin" && view.can_confirm && canDecide ? view.pending_statements.length : 0;
  const conflicts = ctx?.household?.role === "admin" && view.can_confirm && canResolve ? view.conflicts.length : 0;
  // 成员自己交过、还在等管理员确认：不再让 TA 交第二份，只说已交给管理员
  const mine = ctx?.household?.role !== "admin" && view.pending_statements.some(s => s.submitted_by === userId);
  const ask = need && view.body_plan && !mine && canSubmit ? bodyAskOf(view.body_plan, need) : null;
  const waiting = Boolean(need) && mine && said !== BODY_SENT_TO_ADMIN;
  // 常驻那一行（I 10-15）：形象没卡着、没有要确认和处理的，管理员才有；记过以后卡上说一句结果，下次进来又只剩这一行
  const entry = !ask && !toConfirm && !conflicts && !waiting && ctx?.household?.role === "admin" && canSubmit && bodyRecordGroups(view.body_plan).length > 0;
  if (!ask && !toConfirm && !conflicts && !said && !waiting && !entry) return null;
  const setView = (next: BodyProfileView) => queryClient.setQueryData(bodyQueryKey(userId, petId), next);
  const sheet = open ? (
    <Sheet title={BODY_CARD_TITLE} onClose={() => setOpen(false)} className="ps-body-sheet">
      <BodySheet
        petId={petId}
        view={view}
        ask={recording ? null : ask}
        record={recording}
        needed={Boolean(need) && !recording}
        resumeOn={resumeOn}
        showDecisions={!recording && toConfirm > 0}
        showConflicts={!recording && conflicts > 0}
        journal={journal}
        allowed={allowed}
        onView={setView}
        onFinish={(line) => {
          setOpen(false);
          setSaid(line === BODY_SAVED_RESUME && !queryClient.getQueryData<typeof meta.data>(queryKeys.meta)?.capabilities.some(c => c.key === "character.body_resume" && c.status === "available") ? BODY_SAVED : line);
        }}
      />
    </Sheet>
  ) : null;
  if (entry && !said) {
    return (
      <div className="ps-body-entry" data-testid="body-entry">
        <Button variant="ghost" aria-haspopup="dialog" aria-expanded={open} onClick={() => { setRecording(true); setOpen(true); }}>
          {BODY_ENTRY}
        </Button>
        {sheet}
      </div>
    );
  }
  return (
    <section className="ps-look-card ps-body-card" aria-labelledby={titleId} data-testid="body-info">
      <h3 id={titleId}>{BODY_CARD_TITLE}</h3>
      {ask && need ? <p className="ps-body-card__line">{bodyAskLine(need.group)}</p> : null}
      {waiting ? <p className="ps-body-card__line">{BODY_SENT_TO_ADMIN}</p> : null}
      {toConfirm ? <p className="ps-body-card__line">{bodyToConfirmLine(toConfirm)}</p> : null}
      {conflicts ? <p className="ps-body-card__line">{BODY_CONFLICT_LINE}</p> : null}
      {said ? (
        <p className="ps-body-card__said" role="status" tabIndex={-1} ref={saidRef} data-motion="enter">
          {said}
        </p>
      ) : null}
      {ask || toConfirm || conflicts ? (
        <Button variant="secondary" aria-haspopup="dialog" aria-expanded={open} onClick={() => { setSaid(null); setRecording(false); setOpen(true); }}>
          {ask ? BODY_OPEN_ASK : BODY_OPEN_REVIEW}
        </Button>
      ) : entry ? (
        <Button variant="ghost" aria-haspopup="dialog" aria-expanded={open} onClick={() => { setSaid(null); setRecording(true); setOpen(true); }}>
          {BODY_ENTRY}
        </Button>
      ) : null}
      {sheet}
    </section>
  );
}

/**
 * 面板里：待你确认（管理员）→ 补缺的那一组 → 照片和档案不一样（管理员）。
 * 提交补缺的那一组成功以后收起面板、卡上说结果；确认、驳回、处理冲突在面板里逐条做，面板里没有要做的了才收起。
 */
function BodySheet({ petId, view, ask, record = false, needed, resumeOn, showDecisions, showConflicts, journal, allowed, onView, onFinish }: {
  petId: string;
  view: BodyProfileView;
  ask: BodyAsk | null;
  record?: boolean;
  needed: boolean;
  resumeOn: boolean;
  showDecisions: boolean;
  showConflicts: boolean;
  journal: Journal;
  allowed: (slot: string) => boolean;
  onView: (view: BodyProfileView) => void;
  onFinish: (line: string) => void;
}) {
  const { pets } = useServices();
  const [note, setNote] = useState<string | null>(null);
  const write = useBodyWrite(petId, onView, journal, allowed);
  const revision = view.reality_revision ?? 0;
  const groups = record ? bodyRecordGroups(view.body_plan) : [];
  const [group, setGroup] = useState<string | null>(() => (groups.length === 1 ? groups[0] : null));
  const pickId = useId();
  const recordAsk = record && view.body_plan && group ? bodyRecordAskOf(view.body_plan, group as (typeof groups)[number]) : null;
  // 处理完一条以后：面板里没有要做的了就收起，卡上说这一句；还有就在面板里说
  const after = (next: BodyProfileView, line: string) => {
    const left = (showDecisions ? next.pending_statements.length : 0) + (showConflicts ? next.conflicts.length : 0);
    if (!left && !ask) onFinish(line);
    else setNote(line);
  };
  const pendingTitle = useId();
  return (
    <div className="ps-body-sheet__inner">
      {note ? (
        <p className="ps-body-note" role="status">
          {note}
        </p>
      ) : null}
      {write.error ? (
        <p className="ps-form-error" role="alert">
          {write.error}
        </p>
      ) : null}
      {write.uncertain ? <Button variant="secondary" disabled={write.busy} onClick={write.retry}>{BODY_RETRY}</Button> : null}
      {showDecisions && view.pending_statements.length ? (
        <section className="ps-body-section" aria-labelledby={pendingTitle}>
          <h3 id={pendingTitle}>{BODY_PENDING_TITLE}</h3>
          <ul className="ps-body-list">
            {view.pending_statements.map((statement) => (
              <li key={statement.statement_id} className="ps-body-item" data-testid="body-pending">
                <p>{bodyStatementText(statement.items)}</p>
                <div className="ps-body-actions">
                  <Button
                    variant="secondary"
                    disabled={write.busy}
                    onClick={() =>
                      write.send({
                        owner: pets,
                        slot: `confirm:${encodeURIComponent(statement.statement_id)}:${revision}`,
                        run: (key) => pets.confirmBody!(petId, statement.statement_id, { expected_reality_revision: revision }, key),
                        done: (result) => {
                          const next = "body" in result ? result.body : result;
                          after(next, bodyAppliedLine(next, needed, resumeOn));
                        },
                      })
                    }
                  >
                    {BODY_CONFIRM}
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={write.busy}
                    onClick={() =>
                      write.send({
                        owner: pets,
                        slot: `reject:${encodeURIComponent(statement.statement_id)}:${revision}`,
                        run: (key) => pets.rejectBody!(petId, statement.statement_id, key),
                        done: (result) => after("body" in result ? result.body : result, BODY_REJECTED),
                      })
                    }
                  >
                    {BODY_REJECT}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {record ? (
        <>
          <p className="ps-body-note">{BODY_RECORD_NOTE}</p>
          {groups.length > 1 ? (
            <BodyChoices legend={BODY_PICK_GROUP} name={`${pickId}-group`} options={groups.map((g) => ({ value: g, label: BODY_GROUP_NAME[g] }))}
              value={group} onChange={setGroup} disabled={write.busy} />
          ) : null}
          {recordAsk && view.body_plan ? (
            <BodyAskForm
              key={`record:${revision}:${group}`}
              plan={view.body_plan}
              view={view}
              ask={recordAsk}
              busy={write.busy}
              onSubmit={(items) =>
                write.send({
                  owner: pets,
                  slot: `record:${revision}:${JSON.stringify(items)}`,
                  run: (key) => pets.submitBody!(petId, { expected_reality_revision: revision, items }, key),
                  done: (result) => {
                    const outcome = result as BodyStatementResult;
                    onFinish(outcome.status === "pending" ? BODY_SENT_TO_ADMIN : bodyAppliedLine(outcome.body, false, resumeOn));
                  },
                })
              }
            />
          ) : null}
        </>
      ) : null}
      {ask && view.body_plan ? (
        <BodyAskForm
          key={`${revision}:${ask.group ?? "-"}:${ask.parts.join(",")}`}
          plan={view.body_plan}
          view={view}
          ask={ask}
          busy={write.busy}
          onSubmit={(items) =>
            write.send({
              owner: pets,
              slot: `submit:${revision}:${JSON.stringify(items)}`,
              run: (key) => pets.submitBody!(petId, { expected_reality_revision: revision, items }, key),
              done: (result) => {
                const outcome = result as BodyStatementResult;
                onFinish(outcome.status === "pending" ? BODY_SENT_TO_ADMIN : bodyAppliedLine(outcome.body, needed, resumeOn));
              },
            })
          }
        />
      ) : null}
      {showConflicts && view.conflicts.length ? (
        <section className="ps-body-section">
          <ul className="ps-body-list">
            {view.conflicts.map((conflict) => (
              <BodyConflictRow
                key={conflict.conflict_id}
                part={conflict.part_id}
                busy={write.busy}
                onKeep={() =>
                  write.send({
                    owner: pets,
                    slot: `keep:${encodeURIComponent(conflict.conflict_id)}:${revision}`,
                    run: (key) => pets.keepBodyConflict!(petId, conflict.conflict_id, { expected_reality_revision: revision }, key),
                    done: (result) => after("body" in result ? result.body : result, BODY_KEPT),
                  })
                }
                onCorrect={(state) => {
                  const items: BodyStatementItem[] = [{ kind: "part_state", group: null, count: null, part_id: conflict.part_id, state, value: null }];
                  write.send({
                    owner: pets,
                    slot: `correct:${encodeURIComponent(conflict.conflict_id)}:${revision}:${state}`,
                    run: (key) => pets.correctBodyConflict!(petId, conflict.conflict_id, { expected_reality_revision: revision, items }, key),
                    done: (result) => {
                      const next = "body" in result ? result.body : result;
                      after(next, bodyAppliedLine(next, needed, resumeOn));
                    },
                  });
                }}
              />
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

/** 一条照片与档案冲突（§11.3）：说那一句，「按档案」直接处理；「按照片改」先问这一处现在是什么样，再记下 */
function BodyConflictRow({ part, busy, onKeep, onCorrect }: { part: BodyPartId; busy: boolean; onKeep: () => void; onCorrect: (state: BodyStatementState) => void }) {
  const [fixing, setFixing] = useState(false);
  const [state, setState] = useState<BodyStatementState | null>(null);
  const name = useId();
  return (
    <li className="ps-body-item" data-testid="body-conflict">
      <p>
        <span className="ps-body-item__part">{bodyPartLabel(part)}</span>
        {BODY_CONFLICT_LINE}
      </p>
      {fixing ? (
        <>
          <BodyChoices legend={bodyCorrectQuestion(part)} name={name} options={STATE_OPTIONS} value={state} onChange={(next) => setState(next as BodyStatementState)} disabled={busy} />
          <div className="ps-body-actions">
            <Button variant="secondary" disabled={busy || !state} onClick={() => state && onCorrect(state)}>
              {BODY_SUBMIT}
            </Button>
          </div>
        </>
      ) : (
        <div className="ps-body-actions">
          <Button variant="secondary" disabled={busy} onClick={onKeep}>
            {BODY_KEEP}
          </Button>
          <Button variant="secondary" disabled={busy} onClick={() => setFixing(true)}>
            {BODY_CORRECT}
          </Button>
        </div>
      )}
    </li>
  );
}

const STATE_OPTIONS: readonly { value: string; label: string }[] = (["present", "absent", "partial"] as const).map((value) => ({ value, label: BODY_STATE_NAME[value] }));
const DOG_OPTIONS: readonly { value: string; label: string }[] = (["small", "large"] as const).map((value) => ({ value, label: BODY_DOG_SIZE_NAME[value] }));

/** 一组单选（原生单选框叠在 44px 的选项上，写法同建宠页「男孩还是女孩」）。legend 就是这一组的读屏名 */
function BodyChoices({ legend, name, options, value, onChange, disabled, current = false, onFocusRow, rowRef }: {
  legend: string;
  name: string;
  options: readonly { value: string; label: string }[];
  value: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  current?: boolean;
  onFocusRow?: () => void;
  rowRef?: (node: HTMLFieldSetElement | null) => void;
}) {
  return (
    <fieldset className="ps-body-choices" disabled={disabled} data-current={current ? "" : undefined} ref={rowRef}>
      <legend>{legend}</legend>
      <div className="ps-body-choices__row">
        {options.map((option) => (
          <label key={option.value} className="ps-body-choice">
            <input type="radio" name={name} value={option.value} checked={value === option.value} onChange={() => onChange(option.value)} onFocus={onFocusRow} />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** 补缺的那一组：数量 → （和满数不同时）逐个部位 → （狗、还没确认大小时）顺带问大小 → 记下 */
function BodyAskForm({ plan, view, ask, busy, onSubmit }: {
  plan: BodyPlan;
  view: BodyProfileView;
  ask: BodyAsk;
  busy: boolean;
  onSubmit: (items: BodyStatementItem[]) => void;
}) {
  const [form, setForm] = useState<BodyForm>(() => ({ count: bodyDefaultCount(view, ask), states: {}, dog: null }));
  const [current, setCurrent] = useState<BodyPartId | null>(null);
  const rows = useRef(new Map<BodyPartId, HTMLFieldSetElement>());
  const dogAsked = bodyAsksDogSize(view);
  const shown = bodyPartsShown(ask, form.count);
  const check = bodyFormCheck(view, ask, form);
  const base = useId();
  const pick = (part: BodyPartId) => {
    setCurrent(part);
    const row = rows.current.get(part);
    (row?.querySelector<HTMLInputElement>("input:checked") ?? row?.querySelector<HTMLInputElement>("input"))?.focus();
  };
  const counts = Array.from({ length: ask.members.length + 1 }, (_, n) => ask.members.length - n);
  return (
    <form
      className="ps-body-section ps-body-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (check === "ok" && !busy) onSubmit(bodyItemsOf(ask, form, dogAsked));
      }}
    >
      {ask.countable && ask.group ? (
        <BodyChoices
          legend={bodyCountQuestion(ask.group)}
          name={`${base}-count`}
          options={counts.map((n) => ({ value: String(n), label: bodyCountOption(ask.group!, n) }))}
          value={String(form.count)}
          onChange={(next) => setForm((now) => ({ ...now, count: Number(next) }))}
          disabled={busy}
        />
      ) : null}
      {shown ? (
        <fieldset className="ps-body-parts" disabled={busy}>
          <legend>
            {bodyPartsQuestion(ask)}
            <span className="ps-body-parts__side">{BODY_SIDE_NOTE}</span>
          </legend>
          {view.show_body_diagram ? <BodyDiagram plan={plan} parts={ask.parts} states={form.states} current={current} onPick={pick} /> : null}
          {ask.parts.map((part) => (
            <BodyChoices
              key={part}
              legend={bodyPartLabel(part)}
              name={`${base}-${part}`}
              options={STATE_OPTIONS}
              value={form.states[part] ?? null}
              onChange={(next) => {
                setCurrent(part);
                setForm((now) => ({ ...now, states: { ...now.states, [part]: next as BodyStatementState } }));
              }}
              current={current === part}
              onFocusRow={() => setCurrent(part)}
              rowRef={(node) => {
                if (node) rows.current.set(part, node);
                else rows.current.delete(part);
              }}
            />
          ))}
        </fieldset>
      ) : null}
      {dogAsked ? (
        <BodyChoices legend={BODY_DOG_QUESTION} name={`${base}-dog`} options={DOG_OPTIONS} value={form.dog} onChange={(next) => setForm((now) => ({ ...now, dog: next as BodyDogSize }))} disabled={busy} />
      ) : null}
      {check === "mismatch" ? (
        <p className="ps-body-hint" role="status">
          {BODY_MISMATCH}
        </p>
      ) : null}
      <div className="ps-body-actions">
        <Button type="submit" variant="primary" loading={busy} disabled={check !== "ok"}>
          {BODY_SUBMIT}
        </Button>
      </div>
    </form>
  );
}

/**
 * 部位图示（ASSET-UI-032，背面视角：画面左边就是 TA 自己的左边）：整块 aria-hidden；图上「TA 的左边」「TA 的右边」两个小字是前端自己写的。
 * 这次问到的每一处叠一个 44px 的圆形点按区（不进 Tab 顺序），点了把文字列表里那一行选出来；选中、已答用令牌色描边，图里不带选中色。
 */
function BodyDiagram({ plan, parts, states, current, onPick }: {
  plan: BodyPlan;
  parts: readonly BodyPartId[];
  states: BodyForm["states"];
  current: BodyPartId | null;
  onPick: (part: BodyPartId) => void;
}) {
  const round = (value: number) => Math.round(value * 1000) / 1000;
  return (
    <figure className="ps-body-diagram" aria-hidden="true" data-testid="body-diagram">
      <span className="ps-body-diagram__frame">
        <img src={BODY_DIAGRAM_SRC[plan]} alt="" width={240} height={240} />
        <span className="ps-body-diagram__side ps-body-diagram__side--left">← {BODY_DIAGRAM_LEFT}</span>
        <span className="ps-body-diagram__side ps-body-diagram__side--right">{BODY_DIAGRAM_RIGHT} →</span>
        {bodySpotsFor(plan, parts).map(({ part, x, y }) => (
          <button
            key={part}
            type="button"
            tabIndex={-1}
            className="ps-body-spot"
            data-part={part}
            data-current={current === part ? "" : undefined}
            data-answered={states[part] ? "" : undefined}
            style={{ left: `${round((x / 480) * 100)}%`, top: `${round((y / 480) * 100)}%` }}
            onClick={() => onPick(part)}
          />
        ))}
      </span>
      <figcaption>{BODY_DIAGRAM_CAPTION}</figcaption>
    </figure>
  );
}
